import { getTranslation } from '@easy-expense/intl-client';
import { Layout } from '@easy-expense/ui-web-core';
import React from 'react';

import { PlanCard } from './PlanCard.component';
import { computeIndividualPlanCTA, IndividualPlanCTA } from './computeIndividualPlanCTA';
import {
  ANNUAL_DISCOUNT,
  INDIVIDUAL_ANNUAL_BILLED_PRICES,
  INDIVIDUAL_MONTHLY_PRICES,
  INDIVIDUAL_TRIAL_DAYS,
  MONTHLY_PRICES,
  PROFESSIONAL_SEATS,
  TEAM_MIN_SEATS,
  TEAM_TRIAL_DAYS,
  formatPrice,
} from '../../../lib/Billing/constants';
import {
  professionalPlanFeatures,
  starterPlanFeatures,
  teamPlanFeatures,
} from '../../../lib/Billing/planFeatures';
import { BillingTerm, IndividualPlanCode, individualPlanSeats } from '../../../lib/Paddle';

/**
 * The Starter/Professional/Team option-card row shared by the no-subscription paywall and an
 * existing Team subscriber's "Change plan" section — one visual ladder for both entry points.
 */
export const PlanGrid: React.FC<{
  term: BillingTerm;
  offerTrial: boolean;
  changesLocked: boolean;
  currentIndividualPlan: IndividualPlanCode | null;
  isOnAnnualSubscription: boolean;
  // True when the viewer already holds an active Team subscription — individual plans aren't a
  // supported downgrade target from Team, so their CTAs are disabled rather than routed through
  // applySubscriptionChange (which would otherwise reprice the whole team subscription down to a
  // single individual seat).
  hasTeamSubscription: boolean;
  // Active member count of the workspace. An individual plan whose bundled seat allowance is
  // smaller than this can't cover the team, so its CTA is disabled and points at Team instead of
  // selling a plan the customer would be over seats on immediately (EZ-5182).
  seatsUsed: number;
  onPressIndividualPlan: (planCode: IndividualPlanCode) => void;
  teamCTA: string;
  teamCtaDisabled: boolean;
  onPressTeam: () => void;
}> = ({
  term,
  offerTrial,
  changesLocked,
  currentIndividualPlan,
  isOnAnnualSubscription,
  hasTeamSubscription,
  seatsUsed,
  onPressIndividualPlan,
  teamCTA,
  teamCtaDisabled,
  onPressTeam,
}) => {
  const isAnnual = term === 'annual';

  const individualPlanCards = [
    {
      code: 'STARTER' as const,
      title: getTranslation('Starter'),
      description: getTranslation('Track expenses and create reports with ease.'),
      icon: { name: 'leaf-outline', accent: 'success' } as const,
      featured: false,
      featureHeader: getTranslation('Includes:'),
      features: starterPlanFeatures(),
      minUsersLabel: undefined,
      footNote: {
        iconName: 'trending-up-outline' as const,
        title: getTranslation('Need more features?'),
        description: getTranslation('Upgrade anytime as your business grows.'),
      },
    },
    {
      code: 'PROFESSIONAL' as const,
      title: getTranslation('Professional'),
      description: getTranslation('Automate expense tracking and save hours every month.'),
      icon: { name: 'briefcase-outline', accent: 'brandPrimary' } as const,
      featured: true,
      featureHeader: getTranslation('Everything in Starter, plus:'),
      features: professionalPlanFeatures(),
      minUsersLabel: `${PROFESSIONAL_SEATS} ${getTranslation('seats total')}`,
      footNote: undefined,
    },
  ];

  function individualPlanCTALabel(cta: IndividualPlanCTA): string {
    switch (cta) {
      case 'contact-support':
        return getTranslation('Contact support to switch');
      case 'current-plan':
        return getTranslation('Your Current Plan');
      // Naming the reason on the button keeps it readable on touch and by keyboard, where the
      // hover explanation below is unreachable.
      case 'requires-team':
        return getTranslation('Not Enough Seats');
      case 'start-trial':
        return getTranslation('Start Free Trial');
      // offerTrial is false for a returning customer (hasEverSubscribed) even though they
      // hold no current plan -- don't pitch a trial they're not actually eligible for.
      case 'choose-plan':
        return getTranslation('Subscribe');
      case 'switch-to-annual':
        return getTranslation('Switch to annual billing');
      case 'upgrade':
        return getTranslation('Upgrade');
      case 'downgrade':
        return getTranslation('Downgrade');
    }
  }

  /**
   * Hover copy for the 'requires-team' CTA, spelling out both counts so the shortfall is concrete
   * rather than leaving the customer to tally their own workspace.
   *
   * Assembled from raw-string fragments in the `${count} ${getTranslation('noun phrase')}` shape
   * the rest of this card already uses (see minUsersLabel), since getTranslation only ever takes
   * literals.
   */
  function individualPlanCTAInfo(
    cta: IndividualPlanCTA,
    planCode: IndividualPlanCode,
  ): string | undefined {
    if (cta !== 'requires-team') {
      return undefined;
    }
    // Quantity 1 is the plan's bundled allowance -- the same figure computeIndividualPlanCTA
    // compares against to reach 'requires-team' in the first place.
    const seatsIncluded = individualPlanSeats(planCode, 1);
    let seatNoun: string;
    if (seatsIncluded === 1) {
      seatNoun = getTranslation('seat');
    } else {
      seatNoun = getTranslation('seats');
    }
    const allowance = `${getTranslation('This plan only covers')} ${seatsIncluded} ${seatNoun}`;
    // Always plural: this CTA only appears when seatsUsed exceeds an allowance of at least 1, so
    // seatsUsed is 2 or more.
    const usage = `${getTranslation('but your workspace has')} ${seatsUsed} ${getTranslation('members')}`;
    const nextStep = getTranslation('Choose the Team plan to include everyone.');
    return `${allowance}, ${usage}. ${nextStep}`;
  }

  // The Team card advertises a per-seat price. On annual the per-seat rate is
  // the discounted yearly amount ($8/mo -> $72/yr), so the "billed annually
  // per seat" copy and the "per additional seat" line match the billing cycle
  // the toggle is on.
  const additionalSeatPrice = isAnnual
    ? MONTHLY_PRICES.TEAMS_BASIC * 12 * (1 - ANNUAL_DISCOUNT)
    : MONTHLY_PRICES.TEAMS_BASIC;
  const teamPrice = isAnnual
    ? formatPrice(MONTHLY_PRICES.TEAMS_BASIC * (1 - ANNUAL_DISCOUNT))
    : formatPrice(MONTHLY_PRICES.TEAMS_BASIC);
  const teamAnnualBilled = isAnnual
    ? formatPrice(MONTHLY_PRICES.TEAMS_BASIC * 12 * (1 - ANNUAL_DISCOUNT))
    : undefined;
  const teamAnnualFull = isAnnual ? formatPrice(MONTHLY_PRICES.TEAMS_BASIC * 12) : undefined;

  return (
    <Layout.Row style={{ gap: 24, flexWrap: 'wrap' }}>
      {individualPlanCards.map((plan) => {
        const annualBilled = INDIVIDUAL_ANNUAL_BILLED_PRICES[plan.code];
        const annualFull = INDIVIDUAL_MONTHLY_PRICES[plan.code].monthly * 12;
        // Only the user's exact plan *and* billing cycle counts as
        // "current", so switching the same tier between monthly and
        // annual stays actionable instead of showing a disabled card.
        const isCurrentPlan =
          currentIndividualPlan === plan.code && isOnAnnualSubscription === (term === 'annual');
        const cta = computeIndividualPlanCTA({
          planCode: plan.code,
          isCurrentPlan,
          hasTeamSubscription,
          currentIndividualPlan,
          offerTrial,
          seatsUsed,
        });
        return (
          <PlanCard
            key={plan.code}
            title={plan.title}
            description={plan.description}
            icon={plan.icon}
            inverse={plan.featured}
            featured={plan.featured}
            cta={individualPlanCTALabel(cta)}
            ctaInfo={individualPlanCTAInfo(cta, plan.code)}
            price={formatPrice(INDIVIDUAL_MONTHLY_PRICES[plan.code][term])}
            annualBilledPrice={isAnnual ? formatPrice(annualBilled) : undefined}
            annualFullPrice={
              isAnnual && annualFull > annualBilled ? formatPrice(annualFull) : undefined
            }
            term={term}
            featureHeader={plan.featureHeader}
            features={plan.features}
            minUsersLabel={plan.minUsersLabel}
            footNote={plan.footNote}
            trialDays={offerTrial ? INDIVIDUAL_TRIAL_DAYS[plan.code] : undefined}
            // An individual plan the viewer already holds is terminal -- its bundled seat
            // allowance isn't purchasable in units, so there is nothing to press through to,
            // trialing or not (EZ-5172). One that can't seat the whole workspace is terminal
            // too -- only Team can cover them (EZ-5182).
            ctaDisabled={
              hasTeamSubscription || isCurrentPlan || cta === 'requires-team' || changesLocked
            }
            onPress={() => onPressIndividualPlan(plan.code)}
          />
        );
      })}
      <PlanCard
        title={getTranslation('Team')}
        description={getTranslation('For growing businesses managing expenses as a team.')}
        icon={{ name: 'people-outline', accent: 'brandSecondary' }}
        cta={teamCTA}
        price={teamPrice}
        perUser
        annualBilledPrice={teamAnnualBilled}
        annualFullPrice={teamAnnualFull}
        term={term}
        featureHeader={getTranslation('Everything in Professional, plus:')}
        features={teamPlanFeatures()}
        minUsersLabel={`${TEAM_MIN_SEATS}-${getTranslation('seat minimum')} · ${formatPrice(additionalSeatPrice)} ${getTranslation('per additional seat')}`}
        // TEAM_TRIAL_DAYS must match the trial length configured on the
        // prod Team Paddle prices, or the card over-promises at checkout.
        trialDays={offerTrial ? TEAM_TRIAL_DAYS : undefined}
        ctaDisabled={teamCtaDisabled}
        onPress={onPressTeam}
      />
    </Layout.Row>
  );
};
